import { create, StoreApi, UseBoundStore } from 'zustand';

import { SortingState } from '@tanstack/react-table';

import FilterValueArray from 'models/filterValues/FilterValueArray';

import { FilterState } from './types';

export type Sort = {
  id: string;
  desc: boolean;
};

export type HealthPlansFilterState = FilterState<FilterValueArray>;

type HealthPlansActions = {
  setFilters: (filters: HealthPlansFilterState) => void;
  setSort: (sort: SortingState) => void;
  setSearch: (search: string) => void;
  removeFilter: (filterKey: string, id: string) => void;
};

type HealthPlansState = {
  filters: HealthPlansFilterState;
  sort: SortingState;
  search: string;
  actions: HealthPlansActions;
};

export function serializeSort(sort: Sort[]): string {
  return sort.map((s) => `${s.id} ${s.desc ? 'desc' : 'asc'}`).join(',');
}

const createHealthPlansStore = (): [
  useStore: UseBoundStore<StoreApi<HealthPlansState>>,
  useActions: () => HealthPlansActions,
] => {
  const useStore = create<HealthPlansState>((set) => ({
    filters: {},
    sort: [{ id: 'name', desc: false }],
    search: '',
    actions: {
      setFilters: (filters) => set({ filters }),
      setSort: (sort) => set({ sort }),
      setSearch: (search) =>
        set({
          search,
          sort: search ? [{ id: 'search', desc: true }] : [{ id: 'name', desc: false }],
        }),
      removeFilter: (filterKey, id) =>
        set((state) => ({
          filters: {
            ...state.filters,
            ...(state.filters[filterKey] ? { [filterKey]: state.filters[filterKey].removeFilter(id) } : {}),
          },
        })),
    },
  }));

  const useActions = () => useStore((state) => state.actions);

  return [useStore, useActions];
};

export const [useHealthPlansStore, useHealthPlansActions] = createHealthPlansStore();
